import type { PromptDto } from "@/lib/queries";
import type { PromptUsageRowDto } from "@/features/prompts/api/prompts.api";

/**
 * Call-site keys are delimited by dots, dashes, underscores or spaces. A prefix
 * is promoted to a family only once at least two prompts share it, so a unique
 * call site never produces a group of one.
 */
const CALL_SITE_SEPARATOR = /([.\-_\s]+)/;
const MIN_FAMILY_SIZE = 2;

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const RELATIVE_CUTOFF_MS = 30 * MS_PER_DAY;

export type PromptSortKey = "name" | "updated" | "usage";
export type PromptSortDirection = "asc" | "desc";

export interface PromptGroup {
  /** Shared call-site prefix, or null for prompts that share none. */
  family: string | null;
  prompts: PromptDto[];
}

/** Prefixes of a call-site key, most specific first, excluding the key itself. */
function callSitePrefixes(usedIn: string): string[] {
  const parts = usedIn.trim().split(CALL_SITE_SEPARATOR);
  const prefixes: string[] = [];
  for (let end = parts.length - 2; end >= 1; end -= 2) {
    prefixes.push(parts.slice(0, end).join(""));
  }
  return prefixes;
}

function sortPrompts(
  prompts: PromptDto[],
  sortKey: PromptSortKey,
  direction: PromptSortDirection,
  usageByPromptId: ReadonlyMap<string, PromptUsageRowDto>,
): PromptDto[] {
  const factor = direction === "asc" ? 1 : -1;
  return [...prompts].sort((a, b) => {
    if (sortKey === "name") return factor * a.name.localeCompare(b.name);
    if (sortKey === "usage") {
      const aCalls = usageByPromptId.get(a.id)?.calls ?? -1;
      const bCalls = usageByPromptId.get(b.id)?.calls ?? -1;
      return factor * (aCalls - bCalls);
    }
    return factor * (Date.parse(a.updatedAt) - Date.parse(b.updatedAt));
  });
}

/**
 * Buckets anything carrying a call site into families, so a long list scans as
 * a handful of clusters instead of one wall of keys. Families are alphabetical;
 * items sharing none land in a trailing bucket keyed `null`.
 */
function bucketByFamily<T>(
  items: T[],
  getUsedIn: (item: T) => string,
): { family: string | null; items: T[] }[] {
  const prefixCounts = new Map<string, number>();
  for (const item of items) {
    for (const prefix of callSitePrefixes(getUsedIn(item))) {
      prefixCounts.set(prefix, (prefixCounts.get(prefix) ?? 0) + 1);
    }
  }

  const byFamily = new Map<string, T[]>();
  const ungrouped: T[] = [];

  for (const item of items) {
    const family = callSitePrefixes(getUsedIn(item)).find(
      (prefix) => (prefixCounts.get(prefix) ?? 0) >= MIN_FAMILY_SIZE,
    );
    if (family === undefined) {
      ungrouped.push(item);
      continue;
    }
    const bucket = byFamily.get(family);
    if (bucket) bucket.push(item);
    else byFamily.set(family, [item]);
  }

  // A family can be left under-populated once its members are siphoned into a
  // more specific one — those fall back to the ungrouped bucket.
  for (const [family, members] of byFamily) {
    if (members.length < MIN_FAMILY_SIZE) {
      ungrouped.push(...members);
      byFamily.delete(family);
    }
  }

  const buckets = [...byFamily.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([family, members]) => ({ family: family as string | null, items: members }));

  if (ungrouped.length > 0) buckets.push({ family: null, items: ungrouped });

  return buckets;
}

export function groupPromptsByCallSite(
  prompts: PromptDto[],
  sortKey: PromptSortKey,
  direction: PromptSortDirection,
  usageByPromptId: ReadonlyMap<string, PromptUsageRowDto> = new Map(),
): PromptGroup[] {
  return bucketByFamily(prompts, (prompt) => prompt.usedIn).map((bucket) => ({
    family: bucket.family,
    prompts: sortPrompts(bucket.items, sortKey, direction, usageByPromptId),
  }));
}

export interface CallSiteGroup {
  family: string | null;
  callSites: string[];
}

/** The same families as the table, for pickers that list bare call-site keys. */
export function groupCallSites(callSites: string[]): CallSiteGroup[] {
  return bucketByFamily(callSites, (callSite) => callSite).map((bucket) => ({
    family: bucket.family,
    callSites: [...bucket.items].sort((a, b) => a.localeCompare(b)),
  }));
}

/**
 * The part of a call site that is unique to the prompt. The family already
 * carries the shared prefix in the group header, so repeating it on every row
 * is what flattens the two levels into one wall of mono text.
 */
export function callSiteSuffix(usedIn: string, family: string | null): string {
  if (family === null || !usedIn.startsWith(family)) return usedIn;
  return usedIn.slice(family.length).replace(/^[.\-_\s]+/, "") || usedIn;
}

/** Matches name, call site, tenant, model and template body. */
export function filterPromptsBySearch(prompts: PromptDto[], query: string): PromptDto[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return prompts;
  return prompts.filter((prompt) =>
    [
      prompt.name,
      prompt.usedIn,
      prompt.tenantName ?? "",
      prompt.latestVersion?.model ?? "",
      prompt.latestVersion?.content ?? "",
    ].some((field) => field.toLowerCase().includes(needle)),
  );
}

/** Relative for the last 30 days, absolute date beyond that. */
export function formatUpdatedAt(iso: string, locale: string, now: number): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return iso;

  const elapsed = now - then;
  if (elapsed >= RELATIVE_CUTOFF_MS) {
    return new Date(then).toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  const relative = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (elapsed < MS_PER_HOUR) {
    return relative.format(-Math.max(1, Math.round(elapsed / MS_PER_MINUTE)), "minute");
  }
  if (elapsed < MS_PER_DAY) {
    return relative.format(-Math.round(elapsed / MS_PER_HOUR), "hour");
  }
  return relative.format(-Math.round(elapsed / MS_PER_DAY), "day");
}

export function formatExactDateTime(iso: string, locale: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return iso;
  return new Date(then).toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
